'use client';

import { createContext, useContext, ReactNode, useCallback, useMemo } from 'react';

import { CinemaRoomDto } from '@cineamo/api/models/cinema-room/CinemaRoomDto.types';

interface CinemaRoomContextType {
    cinemaRooms: CinemaRoomDto[];
    getCinemaRoomById: (id: CinemaRoomDto['id'] | undefined) => CinemaRoomDto | undefined;
}

const CinemaRoomContext = createContext<CinemaRoomContextType | undefined>(undefined);

interface CinemaRoomProviderProps {
    children: ReactNode;
    cinemaRooms: CinemaRoomDto[];
}

export function CinemaRoomProvider({ children, cinemaRooms }: CinemaRoomProviderProps) {
    const getCinemaRoomById = useCallback(
        (id: CinemaRoomDto['id'] | undefined): CinemaRoomDto | undefined => {
            return cinemaRooms.find((room) => room.id === id);
        },
        [cinemaRooms]
    );

    const value: CinemaRoomContextType = useMemo(
        () => ({
            cinemaRooms,
            getCinemaRoomById
        }),
        [cinemaRooms, getCinemaRoomById]
    );

    return <CinemaRoomContext.Provider value={value}>{children}</CinemaRoomContext.Provider>;
}

export function useCinemaRooms(): CinemaRoomContextType {
    const context = useContext(CinemaRoomContext);
    if (context === undefined) {
        throw new Error('useCinemaRooms must be used within a CinemaRoomProvider');
    }
    return context;
}
